import cloneDeep from "lodash.clonedeep";
import uniqBy from "lodash.uniqby";
import { toast } from "react-toastify";

import {
  GetProcessesResponseBodyType,
  ProcessStatusTableDto,
  WebsocketData,
  WebsocketProcessMessage,
} from "@intelligems/ig-types";

import { QueryClient } from "@tanstack/react-query";

import { getExperience } from "../api/experiences/get-experience";
import {
  experienceKeys,
  historyKeys,
  organizationsKeys,
  processesKeys,
  setExperienceQueryData,
} from "../api/queryKeys";
import { sendWebsocketMessage, VoidDialogProps } from "../store/slices/global";
import { initExperimentFromDto } from "../store/slices/experiences";

import { handleBillingCommandUpdate } from "./handleBillingCommandUpdate";
import { handleShippingProviderPublishProgress } from "./handleShippingProviderPublishProgress";
import { handleShippingConfigurationChanged } from "./handleShippingConfigurationChanged";
import { handleEntityModification } from "./handleEntityModification";
import type { WebsocketMessageHandlerContext } from "./WebsocketMessageHandlerContext";

export function handleWebsocketMessage(
  message: WebsocketData,
  context: WebsocketMessageHandlerContext
) {
  const { dispatch, discountSyncErrorToast, orgId, queryClient } = context;

  dispatch(sendWebsocketMessage(message));

  if (message.type === "configUpdate") {
    void queryClient.invalidateQueries({
      queryKey: historyKeys.allConfigEvents,
    });
  } else if (message.type === "shippingConfigurationChanged") {
    void handleShippingConfigurationChanged(message, orgId, queryClient);
  } else if (message.type === "invalidateOrgSummary") {
    void queryClient.invalidateQueries({
      queryKey: organizationsKeys.orgSummary(orgId),
    });
    void queryClient.invalidateQueries({
      queryKey: [{ scope: "admin", agencies: true, type: "singleAgency" }],
    });
    void queryClient.invalidateQueries({ queryKey: organizationsKeys.one });
  } else if (message.type === "billingCommandUpdate") {
    handleBillingCommandUpdate({
      message,
      orgId,
      queryClient,
      notifyError: toast.error,
    });
  } else if (message.type === "processUpdate") {
    handleProcessUpdate(message, context);
  } else if (message.type === "entityModification") {
    handleEntityModification(message, context);
  } else if (message.type === "syncPricingFunctionComplete") {
    handleSyncPricingFunctionComplete({ orgId, queryClient });
  } else if (message.type === "syncDiscountFunctionStatusChange") {
    discountSyncErrorToast(message.status);
  } else if (message.type === "shippingProviderPublishProgress") {
    handleShippingProviderPublishProgress(message, queryClient);
  }
}

function handleProcessUpdate(
  message: WebsocketProcessMessage,
  context: WebsocketMessageHandlerContext
) {
  const { orgId, queryClient } = context;
  if (message.action === "start") {
    handleProcessUserError(message, context);
    queryClient.setQueryData<GetProcessesResponseBodyType>(
      processesKeys.one(orgId),
      (data) => getProcessesAfterStart(data, message)
    );
  } else if (message.action === "update") {
    queryClient.setQueryData<GetProcessesResponseBodyType>(
      processesKeys.one(orgId),
      (data) => getProcessesAfterUpdate(data, message)
    );
  } else {
    queryClient.setQueryData<GetProcessesResponseBodyType>(
      processesKeys.one(orgId),
      (data) => getProcessesAfterEnd(data, message)
    );
  }
}

function handleProcessUserError(
  message: WebsocketProcessMessage,
  context: WebsocketMessageHandlerContext
) {
  const process = message.process as ProcessStatusTableDto;
  if (process.type !== "userError" || !process?.dialogKey) {
    return;
  }

  const { currentExperience, dispatch, openDialog, queryClient } = context;
  openDialog(process.dialogKey as keyof VoidDialogProps);
  void queryClient.invalidateQueries({
    queryKey: experienceKeys.experimentList,
  });
  if (process.experienceId) {
    void getExperience(process.experienceId).then((data) => {
      if (data.experience) {
        setExperienceQueryData(queryClient, data.experience);
        if (currentExperience?.id === data.experience.id) {
          dispatch(
            initExperimentFromDto({
              experience: data.experience,
              testFlowType: data.experience.type,
            })
          );
        }
      }
    });
  }
}

function getProcessesAfterStart(
  data: GetProcessesResponseBodyType | undefined,
  message: WebsocketProcessMessage
) {
  return {
    processes: uniqBy(
      [message.process as ProcessStatusTableDto, ...(data?.processes || [])],
      (_) => _.type
    ),
  };
}

function getProcessesAfterUpdate(
  data: GetProcessesResponseBodyType | undefined,
  message: WebsocketProcessMessage
) {
  const index = findProcessIndex(data, message);
  if (index === -1) {
    return data;
  }

  const processes: ProcessStatusTableDto[] = cloneDeep(data!.processes);
  if ("loadingPercent" in message.process) {
    if (
      message.process.loadingPercent &&
      message.process.loadingPercent >= (processes[index].loadingPercent || 0)
    ) {
      processes[index] = message.process;
    } else if (message.process.loadingPercent === null) {
      processes[index] = message.process;
    }
  } else {
    processes[index] = message.process as ProcessStatusTableDto;
  }

  return { processes };
}

function getProcessesAfterEnd(
  data: GetProcessesResponseBodyType | undefined,
  message: WebsocketProcessMessage
) {
  const index = findProcessIndex(data, message);
  if (index === -1) {
    return data;
  }

  return {
    processes: data!.processes.toSpliced(index, 1),
  };
}

function handleSyncPricingFunctionComplete({
  orgId,
  queryClient,
}: {
  orgId: string;
  queryClient: QueryClient;
}) {
  void queryClient.invalidateQueries({
    queryKey: organizationsKeys.cartTransforms(orgId),
  });
  void queryClient.invalidateQueries({
    queryKey: organizationsKeys.metafield(orgId),
  });
  queryClient.setQueryData<GetProcessesResponseBodyType>(
    processesKeys.one(orgId),
    (data) => {
      return {
        processes: (data?.processes ?? []).filter(
          (process) => process.type !== "syncPricingFunction"
        ),
      };
    }
  );
}

function findProcessIndex(
  data: { processes?: ProcessStatusTableDto[] } | undefined,
  message: WebsocketProcessMessage
) {
  if (!data?.processes) {
    return -1;
  }
  if (message.process.id) {
    return data.processes.findIndex(
      (process) => process.id === message.process.id
    );
  } else {
    return data.processes.findIndex(
      (process) => process.type === message.process.type
    );
  }
}
